import React from 'react';
import { AlertTriangle, Info, ShieldAlert } from 'lucide-react';
export function HelpCloseOnesSection() {
  return <section id="dopomoga-blyzkij-lyudyni" className="py-20 md:py-28 bg-[#F5F0E8]" data-id="element-514">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-515">
        <div className="text-center mb-12" data-id="element-516">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#0A5E5C] mb-6" data-id="element-517">
            What to do if someone close to you gambles uncontrollably
          </h2>
          <div className="h-1 w-24 bg-[#D4A843] mx-auto rounded-full" data-id="element-518" />
        </div>

        <div className="mb-16" data-id="element-519">
          <p className="text-lg text-gray-700 leading-relaxed bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-520">
            When a loved one faces problem gambling behavior, it can cause
            anxiety, anger, confusion, or powerlessness. It is important to
            realize your emotions and reactions, and try to compose yourself and
            believe that your support and attention can really help if you
            yourself are conscious and calm. It is also important to act
            carefully. Try to start by simply having a calm conversation so that
            the person, who may not yet admit the real state of affairs, feels
            that you are ready to help them without judgment or accusations.
            Your sincere interest and attention to your loved one, their
            emotional state and experiences in their life, your support and love
            can become a fulcrum where your loved one can make a decision to
            admit the problem and choose to act with your support. At the same
            time, do not neglect attention to yourself and your own emotional
            state. Take care of yourself and your resources.
          </p>
        </div>

        <div className="mb-20" data-id="element-521">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#0A5E5C] mb-10 text-center" data-id="element-522">
            Some tips that may help you
          </h3>

          <div className="space-y-6" data-id="element-523">
            {[{
            title: 'Try to understand the problem',
            text: 'Gambling addiction is a complex behavioral problem that can be associated with stress, emotional difficulties, or a desire to avoid problems. Try to understand your loved one who gambles uncontrollably, what they are avoiding, what stress could be the reason for plunging into uncontrolled gambling. If the person is really close to you and you are familiar with their life, you will be able to understand the circumstances of their life that could have caused them to lose control over gambling.'
          }, {
            title: 'Speak calmly and without accusations',
            text: 'Talk to your loved one. Try to build conversations in such a way as to create an atmosphere of trust and acceptance of the situation. Under no circumstances should you judge, blame, or pity. Try to gently invite the person to calmly explore together the state in which the person is, show a sincere interest in what they feel when they play and what prevents them from stopping, what they avoid by plunging into the game, what they lack in real life, why they return to the game and cannot stop. Explore this together and do not judge anything you discover. Such a dialogue, calm discussion, and exchange of thoughts can launch the process of awareness in the person of their problem gambling behavior and become the beginning of real positive changes.'
          }, {
            title: 'Do not try to control everything',
            text: 'Excessive control can lead to conflicts or hiding the problem.'
          }, {
            title: 'Do not cover debts',
            text: 'Paying off debts or constant financial assistance can only delay solving the problem.'
          }, {
            title: 'Encourage seeking help',
            text: 'You can suggest contacting a psychologist, counselor, or support groups. The main thing is not to pressure and try to wait until the person comes to such a decision independently and consciously.'
          }, {
            title: 'Support positive changes',
            text: 'Even small steps — for example, reducing playing time — can be important. Make a plan of small steps together and try to provide support in following it, giving support and your attention.'
          }, {
            title: 'Help find alternative activities',
            text: 'Sports, hobbies, travel, or learning can help replace the time and attention that the game previously occupied. Try to encourage the person to experience new things from new activities, communication, travel, etc., which can captivate them and give positive emotions and positive experiences.'
          }, {
            title: 'Set your own boundaries',
            text: "It is important to determine what is unacceptable to you. Do not give money for gambling and do not take financial responsibility for other people's debts. This also applies to your emotional state."
          }, {
            title: 'Take care of yourself',
            text: 'Living next to a person with an addiction can be emotionally difficult, so it is important not to forget about your own well-being. Helping and supporting a loved one should not devastate you. You will not be able to fully help someone if you yourself have lost your emotional balance. Take care of yourself.'
          }, {
            title: 'Remember that changes take time',
            text: 'Overcoming problem behavior can take time, but the support of loved ones helps to move forward.'
          }].map((tip, index) => <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex gap-4 md:gap-6" data-id="element-524">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0A5E5C]/10 text-[#0A5E5C] font-bold flex items-center justify-center text-lg" data-id="element-525">
                  {index + 1}
                </div>
                <div data-id="element-526">
                  <h4 className="text-xl font-bold text-[#0A5E5C] mb-2" data-id="element-527">
                    {tip.title}
                  </h4>
                  <p className="text-gray-700 leading-relaxed" data-id="element-528">{tip.text}</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* LEGAL PROTECTION SECTION */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-[#0A5E5C] overflow-hidden" data-id="element-529">
          <div className="bg-[#0A5E5C] p-6 text-white flex items-center gap-4" data-id="element-530">
            <ShieldAlert className="w-8 h-8 flex-shrink-0 text-[#D4A843]" data-id="element-531" />
            <h3 className="text-xl md:text-2xl font-serif font-bold" data-id="element-532">
              If there is a threat to the financial security of your family due
              to the uncontrolled gambling of your loved one (relative)
            </h3>
          </div>

          <div className="p-6 md:p-8" data-id="element-533">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-534">
              If your loved one (relative) does not admit the existence of
              problems with gambling due to some circumstances and does not show
              a desire to look for a way out to overcome it, and perhaps
              uncontrolled gambling threatens the financial security of your
              family, you can use some legal tools for protection.
            </p>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8" data-id="element-535">
              <p className="font-medium text-gray-800 leading-relaxed" data-id="element-536">
                Submit an Application to restrict such a person to the State
                Agency "PLAYCITY" through the website{' '}
                <a href="http://www.playcity.gov.ua" className="text-[#0A5E5C] underline hover:text-[#D4A843]" data-id="element-537">
                  www.playcity.gov.ua
                </a>{' '}
                in the "For Citizens" section there is an option "Application
                for restriction", or in written form. As a result of submitting
                such an application, the person will be entered into the
                Register of persons whose access to gambling establishments
                and/or participation in gambling is restricted (hereinafter –
                the Register).
              </p>
            </div>

            <ul className="space-y-6" data-id="element-538">
              <li className="flex items-start gap-4" data-id="element-539">
                <Info className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" data-id="element-540" />
                <p className="text-gray-700 leading-relaxed" data-id="element-541">
                  It is important that you can submit such an application only
                  regarding a first-degree relative, which can be parents,
                  husband, wife, children (including adopted) or, if you are the
                  legal representative of the person who needs to be restricted
                  in access to gambling. Along with the application, you must
                  submit a document confirming your family relationship with the
                  person who needs to be restricted in access to gambling. You
                  must provide a copy of such a document and certify it with
                  your own signature.
                </p>
              </li>

              <li className="flex items-start gap-4" data-id="element-542">
                <AlertTriangle className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" data-id="element-543" />
                <p className="text-gray-700 leading-relaxed" data-id="element-544">
                  An application can only be submitted regarding a person who is
                  21 years old. Persons under 21 years of age cannot be allowed
                  to gamble due to the requirements of current legislation. If a
                  legal gambling operator allowed a person under 21 to gamble,
                  strict liability is provided for such an operator.
                </p>
              </li>

              <li className="flex items-start gap-4" data-id="element-545">
                <Info className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" data-id="element-546" />
                <div data-id="element-547">
                  <p className="text-gray-700 leading-relaxed mb-4" data-id="element-548">
                    It is important to note that the application must be
                    accompanied by justification and documents confirming the
                    need to establish a restriction on access to participation
                    in gambling due to one of the following circumstances:
                  </p>
                  <ol className="list-decimal pl-5 space-y-3 text-gray-700" data-id="element-549">
                    <li className="pl-2" data-id="element-550">
                      exceeding gambling expenses over the person's personal
                      income, which puts such a person or their family in a
                      difficult financial situation;
                    </li>
                    <li className="pl-2" data-id="element-551">
                      exceeding gambling expenses over the person's personal
                      income, which puts the player or their family in a
                      difficult financial situation, which is carried out using
                      borrowed funds, including on the terms of a financial
                      loan;
                    </li>
                    <li className="pl-2" data-id="element-552">
                      the presence of debt obligations in the amount of more
                      than 100 subsistence minimums for citizens' incomes;
                    </li>
                    <li className="pl-2" data-id="element-553">
                      evasion of the person from paying alimony for more than
                      three months;
                    </li>
                    <li className="pl-2" data-id="element-554">
                      if the person or members of their family are recipients of
                      a housing subsidy or benefits for paying for housing and
                      communal services.
                    </li>
                  </ol>
                </div>
              </li>

              <li className="flex items-start gap-4" data-id="element-555">
                <Info className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" data-id="element-556" />
                <p className="text-gray-700 leading-relaxed" data-id="element-557">
                  It is important that by submitting such an application you can
                  restrict the access of the specified person to gambling for a
                  period of up to 6 months. To restrict access for a longer
                  period, it is necessary to apply to the court.
                </p>
              </li>
            </ul>

            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-558">
              <p className="text-gray-600 italic" data-id="element-559">
                If you need more detailed information on this issue, please
                contact us by filling out the form on the site or write to our
                email.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}